// /* eslint-disable no-undef */
/*
 * @file: apiClient.js
 * @description: Contains REST functions for API calls using Axios.
 * @author: Nitin
 */

import axios from "axios";
import querystring from "querystring-es3";
import { getAccessToken, setAuthorizationToken } from "../auth/index";

const DEFAULT_TIMEOUT = 5 * 60 * 1000; // 5-minute timeout for all API requests

// Default headers for API requests
const BASE_HEADERS = {
  "Content-Type": "application/json",
  "Accept-Language": "en",
};

/**
 * Returns language headers along with timeout and referrer module.
 * These headers are included in every API request.
 */
const getLangHeaders = () => ({
  timeout: DEFAULT_TIMEOUT,
  headers: {
    ...BASE_HEADERS,
    lang: "en",
    referrermodule: window.location.href, // Tracks the referring module
  },
});

/**
 * Handles logout on receiving unauthorized (401) or forbidden (403) responses.
 * Dispatches necessary actions to reset authentication state.
 *
 * @param {Object} error - The error response from the API
 * @param {Function} dispatch - Redux dispatch function (optional)
 */
const handleLogout = (error: any, dispatch: any) => {
  if (
    (error?.response?.status === 403 || error?.response?.status === 401) &&
    dispatch
  ) {
    dispatch({ type: "LOGOUT" }); // Logs out the user
    dispatch({ type: "SET_ACTIVE_MAIN_MENU", data: false }); // Resets the menu state
    dispatch({ type: "SET_ACTIVE_MENU_TITLE", data: "" }); // Clears the menu title
  }
};

/**
 * Handles API responses and returns the formatted response data.
 *
 * @param {Object} response - The API response object
 * @returns {Object} - The response data with status
 */
const handleResponse = (response: any) => ({
  ...response?.data,
  status: response?.status,
});

/**
 * Handles API errors and logs them to the console.
 * Also triggers logout if necessary and rejects the request.
 *
 * @param {Object} error - The error response from the API
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @param {Function} reject - Promise reject function
 */
const handleError = (error: any, dispatch: any, reject: any) => {
  handleLogout(error, dispatch); // Handles logout if unauthorized
  reject(error?.response);
};

/**
 * Creates a reusable API method for different HTTP requests.
 *
 * @param {string} method - HTTP method (get, post, put, delete, patch)
 * @returns {Function} - A function that makes the API request
 */
const createApiMethod =
  (method: any) =>
  (url: any, params = {}, dispatch = null) => {
    return new Promise((resolve, reject) => {
      // Set authorization token for secure requests
      setAuthorizationToken(axios, `Bearer ${getAccessToken()}`);

      // Define request configuration based on HTTP method
      const config =
        method === "get" ? { params, ...getLangHeaders() } : getLangHeaders();

      // Convert request body to JSON string for non-GET requests
      const requestData = method === "get" ? null : JSON.stringify(params);

      // Append query parameters to URL for GET requests
      if (method === "get") {
        const query = querystring.stringify(params);
        url = query ? `${url}?${query}` : url;
      }

      // Perform the API request using Axios
      axios[method](url, requestData, config)
        .then((response: any) =>
          // Handle successful response
          resolve(handleResponse(response))
        )
        .catch((error: any) => {
          // Handle API errors and reject the request
          handleError(error, dispatch, reject);
        });
    });
  };

/**
 * API Client Class - Provides static methods for common HTTP requests.
 * Supports JSON and FormData-based requests.
 */
class ApiClient {
  static readonly post = createApiMethod("post"); // Creates a POST request method
  static readonly put = createApiMethod("put"); // Creates a PUT request method
  static readonly get = createApiMethod("get"); // Creates a GET request method
  static readonly patch = createApiMethod("patch"); // Creates a PATCH request method
  static readonly delete = createApiMethod("delete"); // Creates a DELETE request method

  /**
   * Sends a POST request with FormData (for file uploads).
   *
   * @param {string} url - The API endpoint
   * @param {Object} formData - FormData object
   * @param {Function} dispatch - Redux dispatch function (optional)
   * @returns {Promise} - Resolves with API response or rejects with error
   */

  static postFormData(
    url: string,
    formData: FormData,
    dispatch: ((action: any) => void) | null = null
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      setAuthorizationToken(axios, `Bearer ${getAccessToken()}`);

      axios
        .post(url, formData, {
          ...getLangHeaders(),
          headers: {
            "Content-Type": "multipart/form-data", // Required for file uploads
            slug: localStorage.getItem("SLUG") || "", // Custom slug header
          },
        })
        .then((response) => resolve(handleResponse(response)))
        .catch((error) => handleError(error, dispatch, reject));
    });
  }

  /**
   * Sends a PUT request with FormData (for updating file uploads).
   *
   * @param {string} url - The API endpoint
   * @param {Object} formData - FormData object
   * @param {Function} dispatch - Redux dispatch function (optional)
   * @returns {Promise} - Resolves with API response or rejects with error
   */

  static putFormData(
    url: string,
    formData: FormData,
    dispatch: ((action: any) => void) | null = null
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      setAuthorizationToken(axios, `Bearer ${getAccessToken()}`);

      axios
        .put(url, formData, {
          ...getLangHeaders(),
          headers: {
            "Content-Type": "multipart/form-data", // Required for file uploads
            slug: localStorage.getItem("SLUG") || "", // Custom slug header
          },
        })
        .then((response) => resolve(handleResponse(response)))
        .catch((error) => handleError(error, dispatch, reject));
    });
  }

  /**
   * Sends a PATCH request with FormData (for partial file updates).
   *
   * @param {string} url - The API endpoint
   * @param {Object} formData - FormData object
   * @param {Function} dispatch - Redux dispatch function (optional)
   * @returns {Promise} - Resolves with API response or rejects with error
   */

  static patchFormData(
    url: string,
    formData: FormData,
    dispatch: ((action: any) => void) | null = null
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      setAuthorizationToken(axios, `Bearer ${getAccessToken()}`);

      axios
        .patch(url, formData, {
          ...getLangHeaders(),
          headers: {
            "Content-Type": "multipart/form-data", // Required for file uploads
            slug: localStorage.getItem("SLUG") || "", // Custom slug header
          },
        })
        .then((response) => resolve(handleResponse(response)))
        .catch((error) => handleError(error, dispatch, reject));
    });
  }
}

export default ApiClient;
